<template>
  <v-dialog
    :value="dialog"
    fullscreen
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"/>
      <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn
          :disabled="!isModelChanged()"
          tabindex="2"
          class="d-md-none"
          text
          @click="submit">
          {{ $t('save') }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-card>
      <v-form ref="form" :disabled="!hasAccess()"  @submit.stop.prevent>
        <div class="p-dialog-full-screen-container">
          <v-row class="mb-0 mt-0">
            <v-col cols="12" md="7" lg="4">
              <v-text-field
                type="text"
                ref="title"
                v-model.trim="editedProject.title"
                :label="$t('project.edit.addTitle')"
                :rules="rules.title"
                class="p-input-lg"
                tabindex="1"
                clearable
                validate-on-blur
                autofocus
              ></v-text-field>
              <v-textarea
                v-model.trim="editedProject.description"
                :label="$t('project.edit.description')"
                rows="3"
                class="p-input-solo-filled"
                tabindex="1"
                clearable
                auto-grow
                solo
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="5" lg="3">
              <v-btn
                v-if="hasAccess()"
                :disabled="!isModelChanged()"
                color="primary"
                class="mt-4 hidden-sm-and-down"
                @click="submit"
              >
                {{ $t('save') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="mb-8 mt-n8">
            <v-col cols="3" md="3" lg="2">
              <v-text-field
                type="text"
                v-model.trim='editedProject.projectNumber'
                :label="$t('project.edit.projectNumber')"
                :rules="rules.projectNumber"
                :disabled="currentSettings.generateProjectNumber"
                :hint="currentSettings.generateProjectNumber ? $t('project.edit.projectNumber_hint') : ''"
                :persistent-hint="currentSettings.generateProjectNumber"
                tabindex="1"
                clearable
                validate-on-blur
              ></v-text-field>
            </v-col>
            <v-col cols="3" md="3" lg="2">
              <v-text-field
                type="text"
                v-model.trim="editedProject.archiveNumber"
                :label="$t('project.edit.archiveNumber')"
                :rules="rules.archiveNumber"
                tabindex="1"
                clearable
                validate-on-blur
              ></v-text-field>
            </v-col>
            <v-col cols="3" md="3" lg="2">
              <v-text-field
                type="text"
                v-model.trim="editedProject.externalId"
                :label="$t('project.edit.externalId')"
                tabindex="1"
                clearable
                validate-on-blur
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="7" lg="4">
              <label class="p-subtitle-primary mb-5">{{ $t('project.edit.projectDetails') }}</label>
              <v-select
                v-model="editedProject.seasonId"
                :items="seasons"
                :label="$t('project.edit.season')"
                :rules="rules.seasonId"
                item-value="id"
                item-text="name"
                prepend-icon="mdi-calendar-range-outline"
                tabindex="1"
                clearable
              ></v-select>
              <p-combobox
                v-model="editedProject.labels"
                :items="projectLabels"
                :label="$t('project.edit.labels')"
                item-value="id"
                item-text="name"
                prepend-icon="mdi-label-outline"
                tabindex="1"
                clearable
                multiple
                small-chips
                deletable-chips
                hide-selected
              ></p-combobox>
              <v-autocomplete
                ref="managers"
                v-model="editedProject.managers"
                :items="usersShort"
                :rules="rules.managers"
                :label="$t('project.edit.managers')"
                :return-object="false"
                item-value="id"
                item-text="fullName"
                prepend-icon="mdi-calendar-account-outline"
                tabindex="1"
                clearable
                multiple
                small-chips
                deletable-chips
                hide-selected
                @change="$refs.managers.lazySearch=''"
              ></v-autocomplete>
              <div class="pl-9">
                <v-checkbox
                  v-model="editedProject.public"
                  :label="$t('project.edit.public')"
                  tabindex="1"
                  dense
                ></v-checkbox>
                <v-row class="mt-4">
                  <v-col>
                    <div class="subtitle-2">{{ $t('project.edit.status') }}</div>
                    <v-radio-group
                      v-model="editedProject.statusId"
                      dense
                    >
                      <v-radio
                        v-for="status of projectStatuses"
                        :key="status.id"
                        :label="status.name"
                        :value="status.id"
                        tabindex="1"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col>
                    <div class="subtitle-2">{{ $t('project.edit.bookingStatus') }}</div>
                    <v-radio-group
                      v-model="editedProject.bookingStatusId"
                      dense
                    >
                      <v-radio
                        v-for="status of projectBookingStatuses"
                        :key="status.id"
                        :label="status.name"
                        :value="status.id"
                        tabindex="1"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col cols="12" md="5" lg="3">
              <label class="p-subtitle-primary mb-5">{{ $t('project.edit.advanced') }}</label>
              <!-- TODO: advanced management -->
              <v-select
                v-model="editedProject.schemeId"
                :items="schemes"
                :label="$t('project.edit.scheme')"
                item-value="id"
                item-text="name"
                tabindex="1"
                clearable
              ></v-select>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import { projectSchema } from '@/constants'

import PBtnIcon from '@/components/PBtnIcon'
import PCombobox from '@/components/PCombobox'

import projectStatusMixin from '@/mixins/projectStatus.mixin'
import projectBookingStatusMixin from '@/mixins/projectBookingStatus.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PBtnIcon,
    PCombobox
  },


  mixins: [
    projectStatusMixin,
    projectBookingStatusMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    accessMixin
  ],


  props: {
    project: Object
  },


  data () {
    return {
      editedProject: {}
    }
  },


  computed: {
    ...mapGetters('projectLabel', ['projectLabels', 'projectLabelById']),
    ...mapGetters('season', ['currentSeasonId', 'seasons']),
    ...mapGetters('settings', ['currentSettings']),
    ...mapGetters('scheme', ['schemes']),
    ...mapGetters('user', ['usersShort']),

    dialogTitle () {
      if (!this.hasAccess()) {
        return this.$t('project.viewProject')
      }
      return this.project ? this.$t('project.editProject') : this.$t('project.createProject')
    },

    defaultProject () {
      return {
        title: '',
        description: null,
        public: false,
        statusId: 1,
        bookingStatusId: 0,
        labels: [],
        managers: [],
        seasonId: this.currentSeasonId,
        schemeId: null,
        projectNumber: null,
        archiveNumber: null
      }
    },

    rulesToApply () {
      const rule = this.rule
      return {
        title: [
          rule.required(),
          rule.maxLength(projectSchema.titleMaxLength, this.$t('validation.maxLengthStringNamed', { name: this.$t('project.edit.title'), max: projectSchema.titleMaxLength }))
        ],
        projectNumber: [rule.maxLength(projectSchema.projectNumberMaxLength)],
        archiveNumber: [rule.maxLength(projectSchema.archiveNumberMaxLength)],
        seasonId: [rule.required()],
        managers: [rule.required(this.$t('validation.projectManagerRequired'))]
      }
    }
  },


  methods: {
    ...mapActions('project', ['createProject', 'updateProject']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.project) {
        this.removeFromChanges(this.project)
      }
      this.editedProject = this.getObjectCopy(this.project || this.defaultProject)
      this.editedProject.labels = this.editedProject.labels.map(label => {
        return this.projectLabelById(label)
      })
      this.initWatchedModel(this.editedProject)
      this.resetValidation()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const project = this.getObjectCopy(this.editedProject)
        const labels = []
        for (const label of project.labels) {
          if (typeof (label) === 'string') {
            labels.push({ name: label })
          } else {
            labels.push(label)
          }
        }

        project.labels = labels
        if (project.id) {
          await this.updateProject(project)
          this.showSnackbarSuccess(this.$t('project.projectUpdated'))
        } else {
          await this.createProject(project)
          this.showSnackbarSuccess(this.$t('project.projectCreated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
